import { FormEvent, useEffect, useMemo, useState } from 'react';
import { apiDelete, apiGet, apiPatch, apiPost } from '@shared/api/httpClient';

type EnvironmentName = 'dev' | 'test' | 'stage' | 'prod';

type EvaluationUser = {
  userName: string;
  givenName: string;
  surname: string;
  email: string;
  bundleId: string;
  desktopProfile?: string;
  passwordSecretArn?: string;
  resetPasswordOnUpdate?: boolean;
  cognitoUsername?: string;
  applicationGroup?: 'CLIENT_ADMIN' | 'CLIENT_READONLY';
  expiresAt?: string;
};

type DependencyResult = {
  name?: string;
  status?: string;
  message?: string;
};

type EvaluationDeployment = {
  deploymentId: string;
  name?: string;
  company?: string;
  purpose?: string;
  clientId?: string;
  targetAccountId?: string;
  targetRegion?: string;
  targetRoleArn?: string;
  externalIdSecretArn?: string;
  directoryId?: string;
  stackName?: string;
  duoMfaRequired?: boolean;
  evaluatorPolicyConfirmed?: boolean;
  environmentAccess?: Partial<Record<EnvironmentName, boolean>>;
  egressCidrs?: string[];
  evaluatorEgressCidrs?: string[];
  users?: EvaluationUser[];
  dependencyStatus?: string;
  dependencyResults?: DependencyResult[];
  dependencyWarnings?: string[];
  dependencyCheckedAt?: string;
  desiredFingerprint?: string;
  dependencyFingerprint?: string;
  operationStatus?: string;
  stackStatus?: string;
  stackId?: string;
  workflowExecutionArn?: string;
  workspaces?: Array<{ userName?: string; workspaceId?: string; state?: string; bundleId?: string }>;
  lastError?: string;
  createdAt?: string;
  updatedAt?: string;
  deployedAt?: string;
  deletedAt?: string;
};

type DeploymentListResponse = {
  deployments?: EvaluationDeployment[];
  count?: number;
  infrastructureReady?: boolean;
  message?: string;
};
type DeploymentResponse = { deployment: EvaluationDeployment; message?: string; executionArn?: string };

type BundleOption = {
  profile?: string;
  label: string;
  description?: string;
  bundleId?: string;
  protocol?: string;
  operatingSystem?: string;
  cpu?: string;
  memory?: string;
  storage?: string;
};

type BundleResponse = {
  recommendedOptions?: BundleOption[];
  advancedOptions?: BundleOption[];
  options?: BundleOption[];
  cached?: boolean;
  lastRefreshedAt?: string;
  warning?: string;
  message?: string;
};

type DeploymentForm = {
  name: string;
  company: string;
  purpose: string;
  clientId: string;
  targetAccountId: string;
  targetRegion: string;
  targetRoleArn: string;
  externalIdSecretArn: string;
  directoryId: string;
  stackName: string;
  duoMfaRequired: boolean;
  evaluatorPolicyConfirmed: boolean;
  environmentAccess: Record<EnvironmentName, boolean>;
  egressCidrs: string;
  users: EvaluationUser[];
};

type UserDraft = EvaluationUser;

const EMPTY_FORM: DeploymentForm = {
  name: 'Protected Evaluation Access',
  company: '',
  purpose: 'Protected platform evaluation',
  clientId: '',
  targetAccountId: '',
  targetRegion: 'us-east-1',
  targetRoleArn: '',
  externalIdSecretArn: '',
  directoryId: '',
  stackName: '',
  duoMfaRequired: true,
  evaluatorPolicyConfirmed: false,
  environmentAccess: { dev: false, test: false, stage: true, prod: false },
  egressCidrs: '',
  users: [],
};

const EMPTY_USER: UserDraft = {
  userName: '',
  givenName: '',
  surname: '',
  email: '',
  bundleId: '',
  desktopProfile: '',
  passwordSecretArn: '',
  resetPasswordOnUpdate: false,
  cognitoUsername: '',
  applicationGroup: 'CLIENT_READONLY',
  expiresAt: '',
};

const IN_PROGRESS_STATUSES = new Set([
  'CHECKING',
  'DEPLOYING',
  'REFRESHING',
  'DELETE_CHECKING',
  'DELETE_READY',
  'DELETING',
]);

function formatDate(value?: string): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function statusClass(status?: string): string {
  const value = String(status || '').toUpperCase();
  if (value.includes('FAIL') || value.includes('ERROR') || value.includes('ROLLBACK')) return 'status-pill status-pill-error';
  if (value.includes('WARN') || value.includes('PENDING') || value.includes('CHECK')) return 'status-pill status-pill-warning';
  if (value.includes('PASS') || value.includes('DEPLOYED') || value.includes('READY') || value.includes('COMPLETE')) return 'status-pill status-pill-success';
  if (value.includes('DELETE')) return 'status-pill status-pill-muted';
  return 'status-pill status-pill-neutral';
}

function formFromDeployment(deployment: EvaluationDeployment): DeploymentForm {
  return {
    name: deployment.name || 'Protected Evaluation Access',
    company: deployment.company || '',
    purpose: deployment.purpose || 'Protected platform evaluation',
    clientId: deployment.clientId || '',
    targetAccountId: deployment.targetAccountId || '',
    targetRegion: deployment.targetRegion || 'us-east-1',
    targetRoleArn: deployment.targetRoleArn || '',
    externalIdSecretArn: deployment.externalIdSecretArn || '',
    directoryId: deployment.directoryId || '',
    stackName: deployment.stackName || '',
    duoMfaRequired: deployment.duoMfaRequired !== false,
    evaluatorPolicyConfirmed: deployment.evaluatorPolicyConfirmed === true,
    environmentAccess: {
      dev: deployment.environmentAccess?.dev === true,
      test: deployment.environmentAccess?.test === true,
      stage: deployment.environmentAccess?.stage === true,
      prod: deployment.environmentAccess?.prod === true,
    },
    egressCidrs: (deployment.egressCidrs || []).join('\n'),
    users: deployment.users || [],
  };
}

function deploymentPayload(form: DeploymentForm) {
  return {
    ...form,
    egressCidrs: form.egressCidrs
      .split(/[\n,]/)
      .map((value) => value.trim())
      .filter(Boolean),
    users: form.users,
  };
}

function normalizedBundleOptions(response: BundleResponse): BundleOption[] {
  const options = [...(response.recommendedOptions || response.options || []), ...(response.advancedOptions || [])];
  const seen = new Set<string>();
  return options.filter((option) => {
    const key = String(option.bundleId || option.label || '').trim();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function RequiredFieldTitle({ children }: { children: string }) {
  return (
    <span className="field-label-title">
      {children} <span className="required-asterisk" aria-label="required">*</span>
    </span>
  );
}

function isInfrastructureNotReadyMessage(message: string): boolean {
  const normalized = message.toLowerCase();
  return normalized.includes('requested resource not found')
    || normalized.includes('controlled evaluation access infrastructure is not ready');
}

export default function ControlledEvaluationAccessPage() {
  const [deployments, setDeployments] = useState<EvaluationDeployment[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [form, setForm] = useState<DeploymentForm>(EMPTY_FORM);
  const [userDraft, setUserDraft] = useState<UserDraft>(EMPTY_USER);
  const [editingUserName, setEditingUserName] = useState<string>('');
  const [bundleOptions, setBundleOptions] = useState<BundleOption[]>([]);
  const [bundleWarning, setBundleWarning] = useState<string>('');
  const [bundlesLoading, setBundlesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [actionInFlight, setActionInFlight] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [infrastructureReady, setInfrastructureReady] = useState(true);
  const [infrastructureMessage, setInfrastructureMessage] = useState<string>('');

  const selectedDeployment = useMemo(
    () => deployments.find((deployment) => deployment.deploymentId === selectedId),
    [deployments, selectedId],
  );
  const selectedBundle = useMemo(
    () => bundleOptions.find((option) => option.bundleId === userDraft.bundleId),
    [bundleOptions, userDraft.bundleId],
  );
  const hasActiveWorkflow = deployments.some((deployment) => IN_PROGRESS_STATUSES.has(String(deployment.operationStatus || '').toUpperCase()));

  async function loadDeployments(preserveSelection = true) {
    try {
      const response = await apiGet<DeploymentListResponse>('/admin/controlled-evaluation-access/deployments');
      const ready = response.infrastructureReady !== false;
      setInfrastructureReady(ready);
      setInfrastructureMessage(ready ? '' : response.message || 'Controlled Evaluation Access infrastructure is not ready yet.');
      if (!ready) {
        setDeployments([]);
        setSelectedId('');
        setError('');
        return;
      }
      const next = response.deployments || [];
      setDeployments(next);
      setError('');
      if (preserveSelection && selectedId) {
        const refreshed = next.find((deployment) => deployment.deploymentId === selectedId);
        if (refreshed) setForm(formFromDeployment(refreshed));
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unable to load controlled evaluation deployments.';
      if (isInfrastructureNotReadyMessage(errorMessage)) {
        setInfrastructureReady(false);
        setInfrastructureMessage(
          'Controlled Evaluation Access infrastructure is not ready in this environment. Confirm the stack deployment completed, then refresh.',
        );
        setDeployments([]);
        setSelectedId('');
        setError('');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  }

  async function loadBundles(refresh = false) {
    setBundlesLoading(true);
    setBundleWarning('');
    try {
      const response = await apiGet<BundleResponse>('/admin/workspaces-bundles', {
        query: {
          region: form.targetRegion || 'us-east-1',
          includeAdvanced: false,
          refresh,
        },
      });
      const options = normalizedBundleOptions(response);
      setBundleOptions(options);
      setBundleWarning(response.warning || response.message || '');
      if (!userDraft.bundleId && options[0]?.bundleId) {
        setUserDraft((current) => ({ ...current, bundleId: options[0].bundleId || '', desktopProfile: options[0].profile || '' }));
      }
    } catch (err) {
      setBundleWarning(err instanceof Error ? err.message : 'Unable to discover WorkSpaces bundles.');
    } finally {
      setBundlesLoading(false);
    }
  }

  useEffect(() => {
    void loadDeployments(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void loadBundles(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.targetRegion]);

  useEffect(() => {
    if (!hasActiveWorkflow) return undefined;
    const timer = window.setInterval(() => void loadDeployments(true), 7000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveWorkflow, selectedId]);

  function beginNew() {
    setSelectedId('');
    setForm(EMPTY_FORM);
    setUserDraft(EMPTY_USER);
    setEditingUserName('');
    setError('');
    setMessage('');
  }

  function selectDeployment(deployment: EvaluationDeployment) {
    setSelectedId(deployment.deploymentId);
    setForm(formFromDeployment(deployment));
    setUserDraft(EMPTY_USER);
    setEditingUserName('');
    setError('');
    setMessage('');
  }

  function setEnvironment(environment: EnvironmentName, allowed: boolean) {
    if (environment === 'prod' && allowed) {
      const confirmed = window.confirm(
        'Allow this controlled evaluator egress identity to reach PROD? Production remains blocked unless you explicitly confirm.',
      );
      if (!confirmed) return;
    }
    setForm((current) => ({
      ...current,
      environmentAccess: { ...current.environmentAccess, [environment]: allowed },
    }));
  }

  function addOrUpdateUser() {
    const userName = userDraft.userName.trim();
    const email = userDraft.email.trim();
    const bundleId = userDraft.bundleId.trim();
    if (!userName || !email || !bundleId.startsWith('wsb-')) {
      setError('User name, valid email, and a bundle ID beginning with wsb- are required.');
      return;
    }
    const user: EvaluationUser = {
      ...userDraft,
      userName,
      givenName: userDraft.givenName.trim(),
      surname: userDraft.surname.trim(),
      email,
      bundleId,
      passwordSecretArn: userDraft.passwordSecretArn?.trim() || '',
      cognitoUsername: userDraft.cognitoUsername?.trim() || email,
      applicationGroup: userDraft.applicationGroup || 'CLIENT_READONLY',
    };
    setForm((current) => ({
      ...current,
      users: [...current.users.filter((existing) => existing.userName.toLowerCase() !== (editingUserName || userName).toLowerCase()), user],
    }));
    setUserDraft({ ...EMPTY_USER, bundleId: bundleOptions[0]?.bundleId || '' });
    setEditingUserName('');
    setError('');
  }

  function editUser(user: EvaluationUser) {
    setUserDraft({ ...EMPTY_USER, ...user });
    setEditingUserName(user.userName);
  }

  function removeUser(userName: string) {
    setForm((current) => ({
      ...current,
      users: current.users.filter((user) => user.userName !== userName),
    }));
    if (editingUserName === userName) {
      setEditingUserName('');
      setUserDraft(EMPTY_USER);
    }
  }

  async function saveDeployment(event: FormEvent) {
    event.preventDefault();
    if (!infrastructureReady) {
      setError('Controlled Evaluation Access infrastructure must be ready before a deployment can be saved.');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const response = selectedId
        ? await apiPatch<DeploymentResponse>(
            `/admin/controlled-evaluation-access/deployments/${encodeURIComponent(selectedId)}`,
            deploymentPayload(form),
          )
        : await apiPost<DeploymentResponse>('/admin/controlled-evaluation-access/deployments', deploymentPayload(form));
      setSelectedId(response.deployment.deploymentId);
      setForm(formFromDeployment(response.deployment));
      setMessage(response.message || 'Controlled evaluation deployment saved.');
      await loadDeployments(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save the controlled evaluation deployment.');
    } finally {
      setSaving(false);
    }
  }

  async function runAction(
    action: 'check' | 'deploy' | 'refresh' | 'delete',
    deploymentId = selectedId,
  ) {
    if (!deploymentId) return;
    if (action === 'delete') {
      const confirmed = window.confirm(
        'Delete the target controlled evaluation CloudFormation stack? WorkSpaces and stack-managed secrets will be deleted. The SURELINC-Evaluators AD group and directory users are retained by design.',
      );
      if (!confirmed) return;
    }
    setActionInFlight(action);
    setError('');
    setMessage('');
    try {
      const path = `/admin/controlled-evaluation-access/deployments/${encodeURIComponent(deploymentId)}/${action}`;
      const response = action === 'delete'
        ? await apiDelete<DeploymentResponse>(`/admin/controlled-evaluation-access/deployments/${encodeURIComponent(deploymentId)}`)
        : await apiPost<DeploymentResponse>(path, {});
      setMessage(response.message || `${action} operation started.`);
      await loadDeployments(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Unable to start ${action}.`);
    } finally {
      setActionInFlight('');
    }
  }

  const dependencyCurrent = selectedDeployment?.dependencyStatus === 'PASSED'
    && selectedDeployment?.dependencyFingerprint === selectedDeployment?.desiredFingerprint;

  return (
    <div className="page-stack controlled-evaluation-page">
      <div className="page-heading page-heading-with-actions">
        <div>
          <h2>Controlled Evaluation Access</h2>
          <p>
            Deploy and manage protected evaluator WorkSpaces in an approved target AWS account. This workflow is
            SURELINC-controlled and does not use client portal approval.
          </p>
          <p className="muted-text">
            Existing Admin Portal and Client Portal users remain unchanged. Network-origin restrictions apply only
            to evaluator identities provisioned through this workflow.
          </p>
        </div>
        <div className="button-row">
          <button className="secondary-button" type="button" onClick={() => void loadDeployments(true)} disabled={loading}>Refresh</button>
          <button className="primary-button" type="button" onClick={beginNew}>New deployment</button>
        </div>
      </div>

      {error && <p className="error-text" role="alert">{error}</p>}
      {message && <p className="success-text" role="status">{message}</p>}
      {!infrastructureReady && (
        <div className="controlled-evaluation-warning-box" role="status">
          <strong>Controlled Access infrastructure is not ready</strong>
          <p>{infrastructureMessage}</p>
        </div>
      )}

      <section className="card controlled-evaluation-deployments-card">
        <div className="controlled-evaluation-section-heading">
          <div>
            <h3>Deployments</h3>
            <p className="muted-text">Current desired state, workflow status, target stack status, and evaluator desktops.</p>
          </div>
          <span className="result-count">{deployments.length} deployment(s)</span>
        </div>
        {loading && <p>Loading deployments…</p>}
        {!loading && infrastructureReady && deployments.length === 0 && <p className="muted-text">No controlled evaluation deployments have been created.</p>}
        {!loading && !infrastructureReady && <p className="muted-text">Deployment records will be available after the Controlled Evaluation Access stack is ready.</p>}
        {deployments.length > 0 && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Target</th>
                  <th>Environment access</th>
                  <th>Dependencies</th>
                  <th>Deployment</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {deployments.map((deployment) => {
                  const allowed = (['dev', 'test', 'stage', 'prod'] as EnvironmentName[])
                    .filter((environment) => deployment.environmentAccess?.[environment] === true)
                    .map((environment) => environment.toUpperCase());
                  return (
                    <tr key={deployment.deploymentId} className={selectedId === deployment.deploymentId ? 'controlled-evaluation-selected-row' : ''}>
                      <td>
                        <strong>{deployment.name || deployment.deploymentId}</strong>
                        <div className="field-help">{deployment.company || deployment.clientId || '—'}</div>
                      </td>
                      <td>
                        <div className="mono-cell">{deployment.targetAccountId || '—'}</div>
                        <div className="field-help">{deployment.targetRegion || '—'} · {deployment.directoryId || '—'}</div>
                      </td>
                      <td>{allowed.length ? allowed.join(', ') : 'BLOCK ALL'}</td>
                      <td><span className={statusClass(deployment.dependencyStatus)}>{deployment.dependencyStatus || 'NOT_CHECKED'}</span></td>
                      <td>
                        <span className={statusClass(deployment.operationStatus)}>{deployment.operationStatus || 'DRAFT'}</span>
                        <div className="field-help">{deployment.stackStatus || 'Stack not observed'}</div>
                      </td>
                      <td>{formatDate(deployment.updatedAt)}</td>
                      <td>
                        <div className="button-row table-action-stack">
                          <button className="secondary-button table-action-button" type="button" onClick={() => selectDeployment(deployment)}>Manage</button>
                          <button className="secondary-button table-action-button" type="button" onClick={() => void runAction('refresh', deployment.deploymentId)}>Check status</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <form className="card controlled-evaluation-form-card" onSubmit={saveDeployment}>
        <div className="controlled-evaluation-section-heading">
          <div>
            <h3>{selectedId ? 'Manage controlled evaluation deployment' : 'Create controlled evaluation deployment'}</h3>
            <p className="muted-text">Save desired state, run the dependency check, then deploy. Every deploy repeats the check.</p>
          </div>
          {selectedDeployment && <span className={statusClass(selectedDeployment.operationStatus)}>{selectedDeployment.operationStatus || 'DRAFT'}</span>}
        </div>

        <div className="form-grid controlled-evaluation-form-grid">
          <label>
            <RequiredFieldTitle>Deployment name</RequiredFieldTitle>
            <input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} required />
          </label>
          <label>
            <span className="field-label-title">Company / evaluator organization</span>
            <input value={form.company} onChange={(event) => setForm((current) => ({ ...current, company: event.target.value }))} />
          </label>
          <label>
            <RequiredFieldTitle>Assigned client ID</RequiredFieldTitle>
            <input value={form.clientId} onChange={(event) => setForm((current) => ({ ...current, clientId: event.target.value }))} placeholder="stage-evaluation" required />
            <span className="field-help">Written to Cognito custom:clientId so the evaluator remains within one assigned tenant.</span>
          </label>
          <label>
            <span className="field-label-title">Purpose</span>
            <input value={form.purpose} onChange={(event) => setForm((current) => ({ ...current, purpose: event.target.value }))} />
          </label>
          <label>
            <RequiredFieldTitle>Target AWS account ID</RequiredFieldTitle>
            <input value={form.targetAccountId} onChange={(event) => setForm((current) => ({ ...current, targetAccountId: event.target.value.replace(/\D/g, '').slice(0, 12) }))} inputMode="numeric" placeholder="123456789012" required />
          </label>
          <label>
            <RequiredFieldTitle>Target Region</RequiredFieldTitle>
            <input value={form.targetRegion} onChange={(event) => setForm((current) => ({ ...current, targetRegion: event.target.value }))} placeholder="us-east-1" required />
          </label>
          <label className="form-wide">
            <RequiredFieldTitle>Target deployment role ARN</RequiredFieldTitle>
            <input value={form.targetRoleArn} onChange={(event) => setForm((current) => ({ ...current, targetRoleArn: event.target.value }))} placeholder="arn:aws:iam::123456789012:role/Surelinc-ControlledEvaluationDeploymentRole" required />
            <span className="field-help">The workflow assumes this explicit role. It is not tied to the client portal approval path.</span>
          </label>
          <label className="form-wide">
            <span className="field-label-title">External ID secret ARN</span>
            <input value={form.externalIdSecretArn} onChange={(event) => setForm((current) => ({ ...current, externalIdSecretArn: event.target.value }))} placeholder="Optional Secrets Manager ARN" />
            <span className="field-help">Store the STS External ID in Secrets Manager. Do not paste an External ID value into this page.</span>
          </label>
          <label>
            <RequiredFieldTitle>AWS Managed Microsoft AD directory ID</RequiredFieldTitle>
            <input value={form.directoryId} onChange={(event) => setForm((current) => ({ ...current, directoryId: event.target.value }))} placeholder="d-xxxxxxxxxx" required />
          </label>
          <label>
            <span className="field-label-title">Target stack name</span>
            <input value={form.stackName} onChange={(event) => setForm((current) => ({ ...current, stackName: event.target.value }))} placeholder="Generated when blank" />
          </label>
          <label className="form-wide">
            <span className="field-label-title">Explicit evaluator egress CIDRs</span>
            <textarea rows={3} value={form.egressCidrs} onChange={(event) => setForm((current) => ({ ...current, egressCidrs: event.target.value }))} placeholder={'Optional, one CIDR per line\nExample: 203.0.113.10/32'} />
            <span className="field-help">Leave blank to discover NAT Gateway Elastic IPs from the two subnets registered to the WorkSpaces directory. Deployment fails closed if allowed environments have no stable source CIDR.</span>
          </label>
        </div>

        <section className="controlled-evaluation-subsection">
          <h4>Environment access</h4>
          <p className="field-help">Each environment independently allows or blocks API traffic from this controlled evaluator egress identity. PROD defaults to BLOCK.</p>
          <div className="controlled-evaluation-environment-grid">
            {(['dev', 'test', 'stage', 'prod'] as EnvironmentName[]).map((environment) => (
              <label key={environment} className={environment === 'prod' ? 'controlled-evaluation-prod-control' : ''}>
                <span className="field-label-title">{environment.toUpperCase()}</span>
                <select value={form.environmentAccess[environment] ? 'allow' : 'block'} onChange={(event) => setEnvironment(environment, event.target.value === 'allow')}>
                  <option value="block">BLOCK</option>
                  <option value="allow">ALLOW</option>
                </select>
              </label>
            ))}
          </div>
        </section>

        <section className="controlled-evaluation-subsection">
          <h4>Required protection controls</h4>
          <div className="controlled-evaluation-checkbox-list">
            <label>
              <input type="checkbox" checked={form.duoMfaRequired} disabled readOnly />
              <span>
                Require WorkSpaces SAML / Duo MFA readiness during dependency checks{' '}
                <span className="required-asterisk" aria-label="required">*</span>
              </span>
            </label>
            <label>
              <input type="checkbox" checked={form.evaluatorPolicyConfirmed} onChange={(event) => setForm((current) => ({ ...current, evaluatorPolicyConfirmed: event.target.checked }))} required />
              <span>
                Confirm the SURELINC-Evaluators AD group receives the restricted evaluator desktop policy{' '}
                <span className="required-asterisk" aria-label="required">*</span>
              </span>
            </label>
          </div>
          <p className="field-help">The target stack idempotently ensures the AD security group and membership. It never routinely deletes and recreates the group.</p>
        </section>

        <section className="controlled-evaluation-subsection">
          <div className="controlled-evaluation-section-heading">
            <div>
              <h4>Add evaluator <span className="required-asterisk" aria-label="required">*</span></h4>
              <p className="field-help">Adds or updates one WorkSpaces evaluator. At least one evaluator is required. Plaintext passwords are not accepted.</p>
            </div>
            <button className="text-button" type="button" onClick={() => void loadBundles(true)} disabled={bundlesLoading}>{bundlesLoading ? 'Refreshing…' : 'Refresh bundles'}</button>
          </div>
          <div className="form-grid controlled-evaluation-user-grid">
            <label>
              <RequiredFieldTitle>User name</RequiredFieldTitle>
              <input value={userDraft.userName} onChange={(event) => setUserDraft((current) => ({ ...current, userName: event.target.value }))} />
            </label>
            <label>
              <span className="field-label-title">Desktop profile</span>
              <select
                value={userDraft.bundleId}
                onChange={(event) => {
                  const option = bundleOptions.find((candidate) => candidate.bundleId === event.target.value);
                  setUserDraft((current) => ({ ...current, bundleId: event.target.value, desktopProfile: option?.profile || '' }));
                }}
              >
                <option value="">Select a desktop profile</option>
                {bundleOptions.map((option) => <option key={option.bundleId || option.label} value={option.bundleId}>{option.label}{option.bundleId ? ` — ${option.bundleId}` : ''}</option>)}
              </select>
              {bundleWarning && <span className="field-help">{bundleWarning}</span>}
            </label>
            {selectedBundle && (
              <div className="controlled-evaluation-bundle-summary form-wide">
                <strong>{selectedBundle.label}</strong>
                <span>{selectedBundle.description || 'Selected account/Region WorkSpaces bundle.'}</span>
                <span>{[selectedBundle.operatingSystem, selectedBundle.cpu, selectedBundle.memory, selectedBundle.storage, selectedBundle.protocol].filter(Boolean).join(' · ')}</span>
              </div>
            )}
            <label>
              <RequiredFieldTitle>Bundle ID</RequiredFieldTitle>
              <input value={userDraft.bundleId} onChange={(event) => setUserDraft((current) => ({ ...current, bundleId: event.target.value }))} placeholder="wsb-xxxxxxxxx" />
            </label>
            <label>
              <RequiredFieldTitle>Application role</RequiredFieldTitle>
              <select value={userDraft.applicationGroup || 'CLIENT_READONLY'} onChange={(event) => setUserDraft((current) => ({ ...current, applicationGroup: event.target.value as 'CLIENT_ADMIN' | 'CLIENT_READONLY' }))}>
                <option value="CLIENT_READONLY">Client read-only (CLIENT_READONLY)</option>
                <option value="CLIENT_ADMIN">Client administrator (CLIENT_ADMIN)</option>
              </select>
              <span className="field-help">
                Applies only to this controlled evaluator. It does not change existing portal users or ordinary browser
                access. CLIENT_ADMIN also requires the evaluator to have the matching account-administrator membership
                for the assigned client before administrator-only workflows will succeed.
              </span>
            </label>
            <label>
              <span className="field-label-title">First name</span>
              <input value={userDraft.givenName} onChange={(event) => setUserDraft((current) => ({ ...current, givenName: event.target.value }))} />
            </label>
            <label>
              <span className="field-label-title">Last name</span>
              <input value={userDraft.surname} onChange={(event) => setUserDraft((current) => ({ ...current, surname: event.target.value }))} />
            </label>
            <label>
              <RequiredFieldTitle>Email</RequiredFieldTitle>
              <input type="email" value={userDraft.email} onChange={(event) => setUserDraft((current) => ({ ...current, email: event.target.value }))} />
            </label>
            <label>
              <span className="field-label-title">Cognito username</span>
              <input value={userDraft.cognitoUsername || ''} onChange={(event) => setUserDraft((current) => ({ ...current, cognitoUsername: event.target.value }))} placeholder="Defaults to email" />
            </label>
            <label className="form-wide">
              <span className="field-label-title">Password secret ARN</span>
              <input value={userDraft.passwordSecretArn || ''} onChange={(event) => setUserDraft((current) => ({ ...current, passwordSecretArn: event.target.value }))} placeholder="Optional Secrets Manager ARN" />
              <span className="field-help">Leave blank to let the target stack generate a temporary password secret.</span>
            </label>
          </div>
          <div className="button-row">
            <button className="secondary-button" type="button" onClick={addOrUpdateUser}>{editingUserName ? 'Update evaluator' : 'Add evaluator'}</button>
            {editingUserName && <button className="text-button" type="button" onClick={() => { setEditingUserName(''); setUserDraft(EMPTY_USER); }}>Cancel edit</button>}
          </div>

          <div className="controlled-evaluation-user-list">
            <div className="controlled-evaluation-section-heading">
              <strong>Provisioning users</strong>
              <span className="result-count">{form.users.length} user(s)</span>
            </div>
            {form.users.length === 0 ? <p className="muted-text">No evaluator users have been added.</p> : (
              <div className="table-wrap">
                <table>
                  <thead><tr><th>User</th><th>Name</th><th>Email</th><th>Application role</th><th>Bundle ID</th><th>Password secret</th><th>Actions</th></tr></thead>
                  <tbody>
                    {form.users.map((user) => (
                      <tr key={user.userName}>
                        <td className="mono-cell">{user.userName}</td>
                        <td>{[user.givenName, user.surname].filter(Boolean).join(' ') || '—'}</td>
                        <td>{user.email}</td>
                        <td>{user.applicationGroup === 'CLIENT_ADMIN' ? 'Client administrator (CLIENT_ADMIN)' : 'Client read-only (CLIENT_READONLY)'}</td>
                        <td className="mono-cell">{user.bundleId}</td>
                        <td>{user.passwordSecretArn ? 'Provided' : 'Generated by stack'}</td>
                        <td><div className="button-row table-action-stack"><button className="secondary-button table-action-button" type="button" onClick={() => editUser(user)}>Update</button><button className="danger-button table-action-button" type="button" onClick={() => removeUser(user.userName)}>Delete</button></div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        <div className="button-row controlled-evaluation-primary-actions">
          <button className="primary-button" type="submit" disabled={saving || !infrastructureReady}>{saving ? 'Saving…' : selectedId ? 'Save changes' : 'Save deployment'}</button>
          {selectedId && <button className="secondary-button" type="button" onClick={() => void runAction('check')} disabled={Boolean(actionInFlight)}>{actionInFlight === 'check' ? 'Checking…' : 'Check dependencies'}</button>}
          {selectedId && <button className="success-button" type="button" onClick={() => void runAction('deploy')} disabled={Boolean(actionInFlight) || !dependencyCurrent}>{actionInFlight === 'deploy' ? 'Deploying…' : 'Deploy stack'}</button>}
          {selectedId && <button className="secondary-button" type="button" onClick={() => void runAction('refresh')} disabled={Boolean(actionInFlight)}>Refresh status</button>}
          {selectedId && <button className="danger-button" type="button" onClick={() => void runAction('delete')} disabled={Boolean(actionInFlight)}>{actionInFlight === 'delete' ? 'Checking deletion…' : 'Delete stack'}</button>}
        </div>
        {!infrastructureReady && <p className="field-help">Saving is disabled until the Controlled Evaluation Access stack is available.</p>}
        {selectedId && !dependencyCurrent && <p className="field-help">Deploy remains disabled until the current saved configuration passes its dependency check.</p>}
      </form>

      {selectedDeployment && (
        <section className="card controlled-evaluation-status-card">
          <div className="controlled-evaluation-section-heading">
            <div><h3>Dependency and deployment status</h3><p className="muted-text">Last dependency check: {formatDate(selectedDeployment.dependencyCheckedAt)}</p></div>
            <span className={statusClass(selectedDeployment.dependencyStatus)}>{selectedDeployment.dependencyStatus || 'NOT_CHECKED'}</span>
          </div>
          {selectedDeployment.lastError && <p className="error-text">{selectedDeployment.lastError}</p>}
          {(selectedDeployment.dependencyResults || []).length > 0 && (
            <div className="controlled-evaluation-check-list">
              {(selectedDeployment.dependencyResults || []).map((result, index) => (
                <div className="controlled-evaluation-check-row" key={`${result.name || 'check'}-${index}`}>
                  <span className={statusClass(result.status)}>{result.status || 'UNKNOWN'}</span>
                  <div><strong>{result.name || 'Dependency check'}</strong><p>{result.message || '—'}</p></div>
                </div>
              ))}
            </div>
          )}
          {(selectedDeployment.dependencyWarnings || []).length > 0 && (
            <div className="controlled-evaluation-warning-box">
              <strong>Warnings</strong>
              <ul>{(selectedDeployment.dependencyWarnings || []).map((warning) => <li key={warning}>{warning}</li>)}</ul>
            </div>
          )}
          <div className="controlled-evaluation-status-grid">
            <div><span>Target stack</span><strong>{selectedDeployment.stackStatus || 'NOT_DEPLOYED'}</strong></div>
            <div><span>Stack ID</span><strong className="mono-cell">{selectedDeployment.stackId || '—'}</strong></div>
            <div><span>Workflow execution</span><strong className="mono-cell">{selectedDeployment.workflowExecutionArn || '—'}</strong></div>
            <div><span>Effective egress</span><strong>{(selectedDeployment.evaluatorEgressCidrs || []).join(', ') || '—'}</strong></div>
          </div>
          {(selectedDeployment.workspaces || []).length > 0 && (
            <div className="table-wrap">
              <table>
                <thead><tr><th>User</th><th>WorkSpace ID</th><th>State</th><th>Bundle</th></tr></thead>
                <tbody>{(selectedDeployment.workspaces || []).map((workspace) => <tr key={workspace.workspaceId || workspace.userName}><td>{workspace.userName}</td><td className="mono-cell">{workspace.workspaceId || '—'}</td><td><span className={statusClass(workspace.state)}>{workspace.state || 'UNKNOWN'}</span></td><td className="mono-cell">{workspace.bundleId || '—'}</td></tr>)}</tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
